<template>
    <AiWorkspaceShell>
        <template #rail>
            <div class="flex h-full min-h-0 flex-col">
                <div class="border-b border-gray-200 p-4">
                    <TextInput
                        v-model="search"
                        :placeholder="t('vue.ai_tags_search')"
                    />
                    <p class="mt-2 text-xs text-gray-400">
                        {{ t("vue.ai_tags_filter_hint") }}
                    </p>
                </div>

                <div class="flex-1 overflow-y-auto p-2">
                    <p v-if="!tags.length" class="p-3 text-sm text-gray-400">
                        {{ t("vue.ai_tags_empty") }}
                    </p>

                    <div
                        v-for="tag in tags"
                        :key="tag.id"
                        class="group flex items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-gray-50"
                    >
                        <template v-if="editingId === tag.id">
                            <input
                                v-model="editName"
                                type="text"
                                class="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-hidden"
                                @keyup.enter="saveRename(tag.id)"
                                @keyup.esc="cancelRename"
                            />
                            <button
                                type="button"
                                class="shrink-0 cursor-pointer text-green-600 hover:text-green-700"
                                @click="saveRename(tag.id)"
                            >
                                <CheckIcon class="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                class="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
                                @click="cancelRename"
                            >
                                <XMarkIcon class="h-4 w-4" />
                            </button>
                        </template>

                        <template v-else>
                            <input
                                type="checkbox"
                                class="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-[#FF421E] focus:ring-[#FF421E]"
                                :checked="selectedTagIds.includes(tag.id)"
                                @change="toggleFilter(tag.id)"
                            />
                            <button
                                type="button"
                                class="min-w-0 flex-1 cursor-pointer truncate text-left text-sm text-gray-700"
                                @dblclick="startRename(tag)"
                                @click="toggleFilter(tag.id)"
                            >
                                {{ tag.name }}
                            </button>
                            <span
                                class="flex shrink-0 items-center gap-2 text-xs text-gray-400"
                                :title="t('vue.ai_tags_usage_hint')"
                            >
                                <span class="flex items-center gap-0.5">
                                    <ChatBubbleLeftRightIcon
                                        class="h-3.5 w-3.5"
                                    />
                                    {{ tag.chat_count }}
                                </span>
                                <span class="flex items-center gap-0.5">
                                    <DocumentTextIcon class="h-3.5 w-3.5" />
                                    {{ tag.content_count }}
                                </span>
                            </span>
                            <button
                                type="button"
                                class="shrink-0 cursor-pointer text-gray-400 transition hover:text-gray-700"
                                :title="t('vue.ai_tags_rename')"
                                @click="startRename(tag)"
                            >
                                <PencilSquareIcon class="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                class="shrink-0 cursor-pointer text-gray-400 transition hover:text-custom-alert-100"
                                :title="t('vue.delete')"
                                @click="removeTag(tag)"
                            >
                                <TrashIcon class="h-4 w-4" />
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </template>

        <!-- Mixed tagged items. Transparent block; each item is a white card. -->
        <template #main>
            <div
                class="mx-auto flex h-full min-h-0 w-full max-w-[800px] flex-col"
            >
                <div class="flex-1 overflow-y-auto">
                    <p
                        v-if="!items.length"
                        class="p-6 text-center text-sm text-gray-400"
                    >
                        {{ t("vue.ai_tags_no_items") }}
                    </p>

                    <inertia-link
                        v-for="item in items"
                        :key="`${item.type}-${item.id}`"
                        :href="itemLink(item)"
                        class="mb-2 block rounded-xl border border-gray-200 bg-white px-4 py-3 transition hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5"
                    >
                        <div class="flex items-center gap-2">
                            <component
                                :is="typeIcon(item.type)"
                                class="h-4 w-4 shrink-0 text-gray-400"
                            />
                            <span
                                class="text-[11px] font-medium uppercase tracking-wide text-gray-400"
                            >
                                {{ typeLabel(item.type) }}
                            </span>
                            <span
                                v-if="item.date"
                                class="ml-auto text-xs text-gray-400"
                            >
                                {{ formatDate(item.date) }}
                            </span>
                        </div>
                        <p
                            class="mt-0.5 line-clamp-1 text-sm font-medium text-gray-800"
                        >
                            {{ item.title }}
                        </p>
                        <div
                            v-if="item.tags.length"
                            class="mt-1 flex flex-wrap gap-1"
                        >
                            <span
                                v-for="tag in item.tags"
                                :key="tag.slug"
                                class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500"
                            >
                                {{ tag.name }}
                            </span>
                        </div>
                    </inertia-link>
                </div>
            </div>
        </template>
    </AiWorkspaceShell>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type { Component } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    PencilSquareIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import useConfirm from "@/composables/useConfirm";
import type { IAiTag } from "@/types/AiChat/IAiTag";

interface ITagOverview {
    id: number;
    name: string;
    slug: string;
    chat_count: number;
    content_count: number;
    total_count: number;
}

interface ITaggedItem {
    type: "chat" | "content";
    id: number;
    title: string;
    date: string | null;
    tags: IAiTag[];
}

const props = defineProps<{
    tags: ITagOverview[];
    items: ITaggedItem[];
    q: string | null;
    activeTagIds: number[];
}>();

const { t } = useTranslations();
const { show } = useConfirm();

const search = ref(props.q ?? "");
const selectedTagIds = ref<number[]>([...props.activeTagIds]);
const editingId = ref<number | null>(null);
const editName = ref("");
let searchTimer: number | undefined;

const visit = (): void => {
    router.get(
        route("ai-tags.index"),
        { q: search.value, tags: selectedTagIds.value },
        { preserveState: true, preserveScroll: true },
    );
};

const toggleFilter = (id: number): void => {
    selectedTagIds.value = selectedTagIds.value.includes(id)
        ? selectedTagIds.value.filter((current) => current !== id)
        : [...selectedTagIds.value, id];
    visit();
};

watch(search, () => {
    if (searchTimer !== undefined) {
        window.clearTimeout(searchTimer);
    }
    searchTimer = window.setTimeout(visit, 300);
});

const startRename = (tag: ITagOverview): void => {
    editingId.value = tag.id;
    editName.value = tag.name;
};

const cancelRename = (): void => {
    editingId.value = null;
    editName.value = "";
};

const saveRename = (id: number): void => {
    if (editName.value.trim() === "") {
        return;
    }
    router.put(
        route("ai-tags.update", { aiTag: id }),
        { name: editName.value.trim() },
        {
            preserveScroll: true,
            onSuccess: cancelRename,
        },
    );
};

const removeTag = async (tag: ITagOverview): Promise<void> => {
    const confirmed = await show({
        header: t("vue.delete"),
        message: t("vue.ai_tags_delete_confirm"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(route("ai-tags.destroy", { aiTag: tag.id }), {
            preserveScroll: true,
        });
    }
};

const itemLink = (item: ITaggedItem): string =>
    item.type === "chat"
        ? route("ai-chat.show", { chat: item.id })
        : route("ai-content.index", { content: item.id });

const typeIcon = (type: string): Component =>
    type === "chat" ? ChatBubbleLeftRightIcon : DocumentTextIcon;

const typeLabel = (type: string): string =>
    type === "chat"
        ? t("vue.ai_tags_type_chat")
        : t("vue.ai_tags_type_content");

const formatDate = (value: string | null): string => {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};
</script>
